<script>
import SellerSelect from '@/components/users/SellerSelect.vue'
import CustomerSelect from '@/components/customers/CustomerSelect.vue'
import { getLeads, search } from '@/api/customer'
import FeatureSelect from '@/components/cars/edit/FeatureSelect.vue'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import NumberPlateInput from '@/components/ui/inputs/NumberPlateInput.vue'
import SalesChannelSelect from '@/components/dealer/SalesChannelSelect.vue'
import Form from '@/components/ui/widgets/Form.vue'
import FormSection from '@/components/ui/widgets/FormSection.vue'
import FormSectionGrid from '@/components/ui/widgets/FormSectionGrid.vue'
import FormattingHelpers from '@/mixins/FormattingHelpers'
import {
  getDeliveryTypes, getPaymentMethods, getRequiredDocumentsToSell, uploadRequiredDocumentToSell
} from '@/api/cars'
import FeatureToggle from '@/components/cars/edit/FeatureToggle.vue'
import { getBanks } from '@/api/banks'
import FeatureTextarea from '@/components/cars/edit/FeatureTextarea.vue'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import Notice from '@/components/cars/edit/Notice.vue'
import ProvisionOptions from '@/components/cars/provisionOptions.vue'
import { getProvisionAccountings, getUsedProvisionOptions } from '@/api/provisions'
import ProvisionAccountingsTable from '@/components/cars/tables/ProvisionAccountingsTable.vue'
import uniq from 'lodash/uniq'
import flatten from 'lodash/flatten'
import { getPackages } from '@/api/customerportal'
import HandlesBackendValidationErrors from '@/mixins/HandlesBackendValidationErrors'
import CarFileCustomerDocument from '@/components/cars/CarFileCustomerDocument.vue'

export default {
  components: {
    'mia-car-file-customer-document': CarFileCustomerDocument,
    'mia-customer-select': CustomerSelect,
    'mia-feature-input': FeatureInput,
    'mia-feature-select': FeatureSelect,
    'mia-feature-textarea': FeatureTextarea,
    'mia-feature-toggle': FeatureToggle,
    'mia-form-section-grid': FormSectionGrid,
    'mia-form-section': FormSection,
    'mia-form': Form,
    'mia-notice': Notice,
    'mia-number-plate-input': NumberPlateInput,
    'mia-provision-accountings-table': ProvisionAccountingsTable,
    'mia-provision-options': ProvisionOptions,
    'mia-sales-channel-select': SalesChannelSelect,
    'mia-seller-select': SellerSelect,
    'mia-submit-button': SubmitButton
  },

  mixins: [FormattingHelpers, HandlesBackendValidationErrors],

  model: {
    prop: 'form'
  },

  props: {
    form: {
      type: Object,
      required: true
    },

    car: {
      type: Object,
      default() {
        return null
      },
      validate: value => value === null || typeof value === 'object'
    },

    offer: {
      type: Object,
      default() {
        return null
      },
      validate: value => value === null || typeof value === 'object'
    },

    errorsKey: {
      type: String,
      required: true
    },

    vat: {
      type: Number,
      default: 0.19
    },

    withCustomerPortalForm: {
      type: Boolean,
      default: false
    },

    withProvisionsForm: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    selectedCustomer: '',
    customerCache: {},
    customerLeads: [],
    paymentMethods: [],
    banks: [],
    deliveryTypes: [],
    provisions: [],
    usedProvisions: [],
    renderProvisionOptions: false,
    customerPortalPackages: [],
    requiredDocuments: [],
    uploadingCategories: []
  }),

  computed: {
    backendValidationErrorsKey() {
      return this.errorsKey
    },

    statusOptions() {
      if (!this.car) {
        return [{ id: '2', label: 'Bestellt für Kunden' }]
      }

      if (parseInt(this.car.item_status, 10) === 3) {
        return [{ id: '4', label: 'Verkauft' }]
      }

      return [{ id: '2', label: 'Bestellt für Kunden' }]
    },

    grossPricePurchased() {
      if (!this.form.item_price_purchased) {
        return ''
      }

      return parseFloat(this.form.item_price_purchased.replace(',', '.')) * (1 + this.vat)
    },

    grossPricePurchased19() {
      if (!this.form.item_price_purchased) {
        return ''
      }

      return parseFloat(this.form.item_price_purchased.replace(',', '.')) * 1.19
    },

    grossPriceSold() {
      if (!this.form.item_price_sold) {
        return ''
      }

      return parseFloat(this.form.item_price_sold.replace(',', '.')) * (1 + this.vat)
    },

    grossPriceSold19() {
      if (!this.form.item_price_sold) {
        return ''
      }

      return parseFloat(this.form.item_price_sold.replace(',', '.')) * 1.19
    },

    regularProvisions() {
      return this.provisions.filter(provision => provision.item_provision_value > 0)
    },

    filteredProvisions() {
      return this.regularProvisions.filter(provision => (this.hideDeletedProvisions ? provision.item_provision_value > 0 : true))
    },

    defaultTax() {
      if (this.car !== null) {
        return this.car.item_tax === 'default'
      }

      return this.offer !== null
    },

    usedProvisionsWithValue() {
      return this.usedProvisions.filter(provision => provision.item_provision > 0)
    },

    disabledProvisionOptions() {
      return uniq(flatten(this.usedProvisionsWithValue.map(provisionOption => provisionOption.item_excluded_provision_options)))
    },

    hasBookedProvisions() {
      return this.provisions && this.provisions.filter(provision => provision.item_provision_value > 0).length > 0
    }
  },

  watch: {
    form: {
      deep: true,
      immediate: true,
      handler(form) {
        if (form.item_customer_id) {
          this.loadCustomer(form.item_customer_id)
        } else {
          this.selectedCustomer = ''
        }
      }
    },

    selectedCustomer(current) {
      if (current) {
        this.loadCustomerLeads(this.selectedCustomer.id)
        this.initRequiredDocuments()
      } else {
        this.customerLeads = []
      }
    }
  },

  created() {
    if (this.car && this.car.id > 0) {
      this.$store.commit('setCar', this.car)
    }

    this.loadPaymentMethods()
    this.loadBanks()
    this.loadDeliveryTypes()

    if (this.authDealer.item_is_allowed_provisions && this.withProvisionsForm && this.form.item_car_id > 0) {
      this.loadUsedProvisions()
      this.loadAccountings()
    }

    if (this.authDealer.item_is_allowed_customer_portal && this.withCustomerPortalForm) {
      this.loadCustomerPortalPackages()
    }

    this.initRequiredDocuments()

    const updatedForm = { ...this.form }

    if (this.car && parseInt(this.car.item_status, 10) === 3) {
      updatedForm.item_status = '4'
    } else {
      updatedForm.item_status = '2'
    }

    if (this.authDealer.item_is_allowed_advanced_registration && !updatedForm.item_customer_type) {
      updatedForm.item_customer_type = 'customer'
    }

    this.$emit('input', updatedForm)

    this.$nextTick(() => {
      this.renderProvisionOptions = true
    })
  },

  methods: {
    async loadCustomerPortalPackages() {
      this.$wait.start('loading car sell customer portal packages')
      const [error, response] = await getPackages()
      this.$wait.end('loading car sell customer portal packages')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Fehler beim Laden der Auslieferungspakete.']
        })
        return
      }

      this.customerPortalPackages = response
    },

    async loadPaymentMethods() {
      this.paymentMethods = await getPaymentMethods()
    },

    async loadCustomer(customerId) {
      if (!this.customerCache[customerId]) {
        const customers = await search('', { id: customerId })
        // eslint-disable-next-line prefer-destructuring
        this.customerCache[customerId] = customers[0]
      }

      this.selectedCustomer = this.customerCache[customerId]
    },

    async loadDeliveryTypes() {
      const [error, response] = await getDeliveryTypes()

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Lieferungstypen konnten nicht geladen werden.']
        })
        return
      }

      this.deliveryTypes = response.data
    },

    async loadCustomerLeads(customerId) {
      const [error, response] = await getLeads(customerId)

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Kunden-Leads konnten nicht geladen werden.']
        })
        return
      }

      this.customerLeads = response
    },

    onCustomerInput(customer) {
      if (customer) {
        this.customerCache[customer.id] = customer
      }

      this.$emit('input', { ...this.form, item_customer_id: customer ? customer.id : null })
      this.clearErrors('item_customer_id')
    },

    createCustomer() {
      mia.common.itemCustomerAjax.show({
        callback: async (data) => {
          if (data.success) {
            this.$nextTick(() => {
              this.car.item_customer = data.customerId
              window.eventBus.$emit('mia-info', {
                info: ['Neukunde erfolgreich angelegt.']
              })
            })
          } else {
            window.eventBus.$emit('mia-error', {
              error: ['Fehler beim Anlegen des Neukunden.']
            })
          }
        }
      })
    },

    async loadBanks() {
      this.banks = await getBanks()
    },

    loadProvisions() {
      this.loadUsedProvisions()
      this.loadAccountings()
    },

    async loadAccountings() {
      this.$wait.start('loading sell car provision accountings')
      const accountings = await getProvisionAccountings(this.form.item_car_id)
      this.$wait.end('loading sell car provision accountings')

      this.provisions = accountings.provisions
    },

    async loadUsedProvisions() {
      this.$wait.start('loading sell car used provision options')
      this.usedProvisions = await getUsedProvisionOptions(this.car.id)
      this.$wait.end('loading sell car used provision options')
    },

    onFormInput(field, value) {
      this.$emit('input', { ...this.form, [field]: value })
      this.clearErrors(field)
    },

    onCustomerPortalFormInput(field, value) {
      this.$emit('input', {
        ...this.form,
        customer_portal: {
          ...this.form.customer_portal,
          [field]: value
        }
      })
      this.clearErrors(`customer_portal.${field}`)
    },

    scrollToTop() {
      const main = document.querySelector('main.right')

      if (!main) {
        return
      }

      main.scrollTo({
        top: 0,
        behavior: 'instant'
      })
    },

    scrollToBottom() {
      const main = document.querySelector('main.right')

      if (!main) {
        return
      }

      main.scrollTo({
        top: main.scrollHeight,
        behavior: 'smooth'
      })
    },

    initRequiredDocuments() {
      if (this.authDealer.item_is_allowed_car_sell_required_documents) {
        this.loadRequiredDocuments()
      }
    },

    async loadRequiredDocuments() {
      if (!this.form.item_customer_id || this.$wait.is('loading car sell required documents')) {
        return
      }

      this.$wait.start('loading car sell required documents')
      const [error, response] = await getRequiredDocumentsToSell(this.car ? this.car.id : 0, this.form.item_customer_id)
      this.$wait.end('loading car sell required documents')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Fehler beim Laden der Pflichtdokumente.']
        })
        return
      }

      this.requiredDocuments = response.data
    },

    async onDocumentUpload({ file, category }) {
      if (!this.car) {
        this.onFormInput('required_documents', [...this.requiredDocuments, { file, category }])
        return
      }

      if (this.uploadingCategories.includes(category.id)) {
        return
      }

      this.uploadingCategories = [...this.uploadingCategories, category.id]

      const formData = new FormData()
      formData.set('file', file)
      formData.set('item_category_id', category.id)
      formData.set('item_customer_id', this.form.item_customer_id)

      const [error, response] = await uploadRequiredDocumentToSell(this.car.id, formData)
      this.uploadingCategories = this.uploadingCategories.filter(id => id !== category.id)

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Fehler beim Hochladen des Dokuments.']
        })
      }

      this.requiredDocuments = response.data
      window.eventBus.$emit('mia-info', {
        info: ['Dokument erfolgreich hochgeladen.']
      })
    },

    onDocumentDeleted(category) {
      const updatedDocuments = this.form.required_documents.filter(existingItem => existingItem.category.id !== category.id)

      this.onFormInput('required_documents', updatedDocuments)
    }
  }
}
</script>

<template>
  <mia-form
    sticky
    footer-style="bottom: -0.5rem"
    footer-sticking-class="rounded-t-md"
    footer-sticking-style="box-shadow: 0px -7px 15px 5px rgba(0,0,0,0.12)"
    @submit="$emit('submit', form)"
  >
    <template #default>
      <mia-form-section
        title="Verkaufsmeldung"
        vertical
      >
        <mia-form-section-grid :cols="4">
          <div class="col-span-2">
            <label class="mb-1 mr-2 tracking-wider text-gray-800">Verkäufer</label>

            <mia-seller-select
              :value="form.item_seller_id"
              :errors="getErrors('item_seller_id')"
              required
              @input="onFormInput('item_seller_id', $event)"
            />
          </div>

          <div class="col-span-2">
            <label class="mb-1 mr-2 tracking-wider text-gray-800">Nach Verkauf zuständig</label>

            <mia-seller-select
              :value="form.item_assignee_id"
              :errors="getErrors('item_assignee_id')"
              required
              @input="onFormInput('item_assignee_id', $event)"
            />
          </div>

          <div class="col-span-2">
            <label class="mb-1 mr-2 tracking-wider text-gray-800">Käufer</label>

            <mia-customer-select
              :value="selectedCustomer"
              :errors="getErrors('item_customer_id')"
              required
              @input="onCustomerInput"
            />
            <button
              class="mt-2 inline-flex items-center px-3 py-2 text-sm transition-colors duration-100 ease-out bg-orange-mia rounded shadow-xs text-white focus:outline-none focus:shadow-outline hover:bg-orange-mia-dark"
              type="button"
              @click="createCustomer"
            >
              Neukunden anlegen
            </button>
          </div>

          <div class="col-span-2">
            <mia-has-feature feature="leads">
              <template v-if="selectedCustomer">
                <template v-if="customerLeads.length === 0">
                  <label class="mb-1 mr-2 tracking-wider text-gray-800">Lead Vorgang</label>
                  <div class="italic mt-2">
                    Keine Leads für gewählten Käufer vorhanden.
                  </div>
                </template>
                <mia-feature-select
                  v-else
                  :value="form.item_lead_process"
                  :errors="getErrors('item_lead_process')"
                  label="Lead Vorgang"
                  @input="onFormInput('item_lead_process', $event)"
                >
                  <template #options>
                    <option value="">
                      Lead Vorgang auswählen
                    </option>
                    <option
                      v-for="lead in customerLeads"
                      :key="lead.id"
                      :value="lead.id"
                    >
                      {{ lead.item_alt_title ? lead.item_alt_title : lead.item_lead_type_title }}
                    </option>
                  </template>
                </mia-feature-select>
              </template>
              <template v-else>
                <label class="mb-1 mr-2 tracking-wider text-gray-800">Lead Vorgang</label>
                <div class="italic mt-2">
                  Bitte wählen Sie zunächst einen Käufer aus.
                </div>
              </template>
            </mia-has-feature>
          </div>

          <div class="col-span-4">
            <hr class="border-gray-300">
          </div>

          <div>
            <mia-feature-input
              label="Datum Kaufvertrag"
              required
              :value="form.item_contract_date"
              :errors="getErrors('item_contract_date')"
              type="date"
              @input="onFormInput('item_contract_date', $event)"
            />
          </div>

          <div class="relative">
            <label class="mb-1 mr-2 tracking-wider text-gray-800">Kennzeichen</label>
            <mia-number-plate-input
              :value="form.item_numberplate"
              :has-errors="hasErrors('item_numberplate')"
              @input="onFormInput('item_numberplate', $event)"
            />

            <mia-fade-transition>
              <small
                v-if="hasErrors('item_numberplate')"
                class="absolute bottom-0 block leading-3 text-red-600 pointer-events-none"
                style="transform: translateY(17px)"
              >
                {{ firstError('item_numberplate') }}
              </small>
            </mia-fade-transition>
          </div>

          <div>
            <mia-feature-select
              label="Status"
              :value="form.item_status"
              :errors="getErrors('item_status')"
              required
              @input="onFormInput('item_status', $event)"
            >
              <template #options>
                <option
                  v-for="s in statusOptions"
                  :key="s.id"
                  :value="s.id"
                >
                  {{ s.label }}
                </option>
              </template>
            </mia-feature-select>
          </div>

          <div>
            <label class="mb-1 mr-2 tracking-wider text-gray-800">Vertriebskanal</label>
            <mia-sales-channel-select
              :value="form.item_sold_channel"
              :errors="getErrors('item_sold_channel')"
              required
              @input="onFormInput('item_sold_channel', $event)"
            />
          </div>

          <div v-if="offer || car.own_car">
            <mia-feature-input
              :label="`${defaultTax ? 'Netto ' : ''} Einkaufspreis in Euro`"
              required
              :value="form.item_price_purchased"
              :errors="getErrors('item_price_purchased')"
              input-class="text-right"
              addon="€"
              @input="onFormInput('item_price_purchased', $event)"
            />
            <template v-if="defaultTax">
              <div
                v-if="vat !== 0.19"
                class="mt-1 text-sm flex items-center justify-between"
              >
                <div class="font-semibold">
                  Ergibt Brutto <small>(inkl. {{ vat * 100 }}% MwSt.)</small>:
                </div>
                <div class="text-right proportional-nums">
                  {{ formatAsCurrency(grossPricePurchased) }}
                </div>
              </div>
              <div class="mt-1 text-sm flex items-center justify-between">
                <div class="font-semibold">
                  Ergibt Brutto <small>(inkl. 19% MwSt.)</small>:
                </div>
                <div class="text-right proportional-nums">
                  {{ formatAsCurrency(grossPricePurchased19) }}
                </div>
              </div>
            </template>
          </div>

          <div>
            <mia-feature-input
              :label="`${defaultTax ? 'Netto ' : ''} Verkaufspreis in Euro`"
              required
              :value="form.item_price_sold"
              :errors="getErrors('item_price_sold')"
              input-class="text-right"
              addon="€"
              @input="onFormInput('item_price_sold', $event)"
            />
            <template v-if="defaultTax">
              <div
                v-if="vat !== 0.19"
                class="mt-1 text-sm flex items-center justify-between"
              >
                <div class="font-semibold">
                  Ergibt Brutto <small>(inkl. {{ vat * 100 }}% MwSt.)</small>:
                </div>
                <div class="text-right proportional-nums">
                  {{ formatAsCurrency(grossPriceSold) }}
                </div>
              </div>
              <div class="mt-1 text-sm flex items-center justify-between">
                <div class="font-semibold">
                  Ergibt Brutto <small>(inkl. 19% MwSt.)</small>:
                </div>
                <div class="text-right proportional-nums">
                  {{ formatAsCurrency(grossPriceSold19) }}
                </div>
              </div>
            </template>
          </div>

          <mia-has-feature feature="advanced_registration">
            <div>
              <mia-feature-select
                label="Verkauf an"
                :value="form.item_customer_type"
                :errors="getErrors('item_customer_type')"
                required
                @input="onFormInput('item_customer_type', $event)"
              >
                <template #options>
                  <option value="customer">
                    Endkunde
                  </option>
                  <option value="reseller">
                    Wiederverkäufer
                  </option>
                </template>
              </mia-feature-select>
            </div>
          </mia-has-feature>

          <div class="col-span-4">
            <hr class="border-gray-300">
          </div>

          <div>
            <mia-feature-select
              label="Zahlmethode"
              :value="form.item_payment_method"
              :errors="getErrors('item_payment_method')"
              required
              @input="onFormInput('item_payment_method', $event)"
            >
              <template #options>
                <option value="">
                  Bitte wählen
                </option>
                <option
                  v-for="paymentMethod in paymentMethods"
                  :key="paymentMethod.id"
                  :value="paymentMethod.id"
                >
                  {{ paymentMethod.label }}
                </option>
              </template>
            </mia-feature-select>
          </div>

          <template v-if="form.item_payment_method && form.item_payment_method !== 'cash'">
            <div>
              <mia-feature-toggle
                label="Kreditschutzbrief"
                :value="form.item_ksb === 'Y'"
                :errors="getErrors('item_ksb')"
                label-position="top"
                @input="onFormInput('item_ksb', $event ? 'Y' : 'N')"
              />
            </div>

            <div>
              <mia-feature-select
                label="Bank"
                :value="form.item_bank"
                :errors="getErrors('item_bank')"
                required
                @input="onFormInput('item_bank', $event)"
              >
                <template #options>
                  <option value="">
                    Bitte wählen
                  </option>
                  <option
                    v-for="bank in banks"
                    :key="bank.id"
                    :value="bank.id"
                  >
                    {{ bank.name }}
                  </option>
                </template>
              </mia-feature-select>
            </div>

            <div>
              <mia-feature-input
                v-if="form.item_bank === '3'"
                label="Datum Genehmigung"
                required
                :value="form.item_bdk_registration_date"
                :errors="getErrors('item_bdk_registration_date')"
                type="date"
                @input="onFormInput('item_bdk_registration_date', $event)"
              />
            </div>

            <div>
              <mia-feature-input
                label="Darlehenssumme"
                required
                :value="form.item_loan"
                :errors="getErrors('item_loan')"
                input-class="text-right"
                addon="€"
                @input="onFormInput('item_loan', $event)"
              />
            </div>

            <div>
              <mia-feature-select
                label="Laufzeit"
                required
                :value="form.item_financing_term"
                :errors="getErrors('item_financing_term')"
                @input="onFormInput('item_financing_term', $event)"
              >
                <template #options>
                  <option value="0">
                    Bitte wählen
                  </option>
                  <option
                    v-for="month in 96"
                    :key="month"
                    :value="month"
                  >
                    {{ month }} Monat{{ month === 1 ? '' : 'e' }}
                  </option>
                </template>
              </mia-feature-select>
            </div>
          </template>

          <div class="col-span-4">
            <hr class="border-gray-300">
          </div>

          <div>
            <mia-feature-toggle
              label="Kunde an Umfrage beteiligen"
              :value="form.item_survey === 'Y'"
              :errors="getErrors('item_survey')"
              label-position="top"
              @input="onFormInput('item_survey', $event ? 'Y' : 'N')"
            />
          </div>

          <div>
            <mia-feature-input
              label="Erwartete Auslieferung"
              required
              :value="form.item_expected_dispatch"
              :errors="getErrors('item_expected_dispatch')"
              type="date"
              @input="onFormInput('item_expected_dispatch', $event)"
            />
          </div>

          <div v-if="[1, 2].includes(parseInt(authDealer.id, 10))">
            <mia-feature-select
              label="Lieferung"
              required
              :value="form.item_delivery_type"
              :errors="getErrors('item_delivery_type')"
              @input="onFormInput('item_delivery_type', $event)"
            >
              <template #options>
                <option value="">
                  Bitte wählen
                </option>
                <option
                  v-for="deliveryType in deliveryTypes"
                  :key="deliveryType.id"
                  :value="deliveryType.id"
                >
                  {{ deliveryType.label }}
                </option>
              </template>
            </mia-feature-select>
          </div>

          <div>
            <mia-feature-toggle
              label="Versicherung verkauft"
              :value="form.item_insurance_sold === 'Y'"
              :errors="getErrors('item_insurance_sold')"
              label-position="top"
              @input="onFormInput('item_insurance_sold', $event ? 'Y' : 'N')"
            />
          </div>

          <div class="col-span-4">
            <hr class="border-gray-300">
          </div>

          <div class="col-span-2">
            <mia-feature-textarea
              label="Bemerkung für Provision"
              :value="form.item_provision_remark"
              :errors="getErrors('item_provision_remark')"
              label-position="top"
              @input="onFormInput('item_provision_remark', $event)"
            />
          </div>
        </mia-form-section-grid>
      </mia-form-section>

      <mia-has-feature feature="car_sell_required_documents">
        <mia-form-section
          title="Pflichtdokumente"
          vertical
        >
          <mia-notice
            v-if="!form.item_customer_id"
            permanent
            type="info"
            class="text-sm"
          >
            <p class="font-semibold">
              Ohne Kundenzuordnung
            </p>
            <p class="mb-0">
              Dem Fahrzeug ist noch kein Kunde zugewiesen. Wählen Sie bitte zunächst einen Kunden aus um fortfahren zu können.
            </p>
          </mia-notice>
          <div
            v-else
            class="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4"
          >
            <mia-car-file-customer-document
              v-for="category in requiredDocuments"
              :key="category.id"
              :category="category.meta"
              :existing-file="category.file"
              :is-uploading="uploadingCategories.includes(category.id)"
              :error="hasErrors(`item_required_documents.${category.id}`) ? firstError(`item_required_documents.${category.id}`) : null"
              :temporary="!car"
              @upload="onDocumentUpload"
              @deleted="onDocumentDeleted(category, $event)"
            />
          </div>
        </mia-form-section>
      </mia-has-feature>

      <mia-has-feature
        v-if="withCustomerPortalForm"
        feature="customer_portal"
      >
        <mia-form-section
          title="Kundenportal"
          vertical
        >
          <mia-notice
            v-if="!form.item_customer_id"
            permanent
            type="info"
            class="text-sm"
          >
            <p class="font-semibold">
              Ohne Kundenzuordnung
            </p>
            <p class="mb-0">
              Dem Fahrzeug ist noch kein Kunde zugewiesen. Hinterlegen Sie bitte zunächst den Kunden um das Kundenportal freischalten zu können.<br>Alternativ kann der Zugang zum Kundenportal über den Bereich <router-link
                to="/customers"
                class="hover:underline"
              >
                Kunden
              </router-link><span v-if="form.item_car_id > 0"> oder beim <router-link
                :to="`/cars/${form.item_car_id}/disponieren`"
                class="hover:underline"
              >Starten der Fahrzeugauslieferung</router-link> freigeschaltet werden.</span>
            </p>
          </mia-notice>
          <mia-notice
            v-else-if="selectedCustomer && selectedCustomer.item_customerportal === 'Y'"
            permanent
            type="success"
          >
            <p class="font-semibold">
              Kundenportal aktiv
            </p>
            <p class="mb-0">
              Der Zugang zum Kundenportal ist für diesen Kunden bereits aktiv.
            </p>
          </mia-notice>
          <mia-form-section-grid v-else-if="selectedCustomer">
            <div>
              <mia-feature-toggle
                label="Kunde für das Kundenportal freischalten"
                :value="form.item_customer_portal_onboarding === 'Y'"
                :errors="getErrors('item_customer_portal_onboarding')"
                label-position="top"
                @input="onFormInput('item_customer_portal_onboarding', $event ? 'Y' : 'N')"
              />
            </div>
          </mia-form-section-grid>
        </mia-form-section>
      </mia-has-feature>

      <mia-has-feature
        v-if="withProvisionsForm"
        feature="provisions"
      >
        <mia-form-section
          title="Prämien"
          vertical
        >
          <mia-wait :for="['loading provision accountings', 'loading used provision options']">
            <template #waiting>
              <div class="flex items-center justify-center py-8">
                <mia-wave-loader />
              </div>
            </template>

            <template #default>
              <div class="text-base font-semibold">
                Bereits verbuchte Prämien
              </div>
              <mia-provision-accountings-table
                class="mt-2"
                :provisions="filteredProvisions"
                :car="car"
                :columns="['created', 'provision_name', 'provision_value', 'real', 'type', 'target', 'actions']"
                :with-delete-action="false"
                without-stats
                @provisions-changed="loadProvisions"
                @reload="loadProvisions"
              />

              <div class="mt-6 text-base font-semibold">
                Weitere Prämien buchen
              </div>
              <mia-provision-options
                v-if="renderProvisionOptions"
                class="mt-2"
                :price="car.item_price_purchased"
                :net-list-price="car.item_net_list_price"
                :disabled-options="disabledProvisionOptions"
                :disable-provision-groups="hasBookedProvisions"
                toggleable
                default-collapsed
                @booked="loadProvisions"
              />
            </template>
          </mia-wait>
        </mia-form-section>
      </mia-has-feature>

      <mia-has-feature feature="customer_portal">
        <mia-form-section
          title="Auslieferungsportal"
          vertical
        >
          <mia-notice
            type="info"
            permanent
          >
            <p>An dieser Stelle kann optional bereits eine Vorauswahl der Auslieferungspakete für das Auslieferungsportal getroffen werden.</p>
            <p class="mb-0">
              <b>Vorausgewählte</b> Pakete werden dem Kunden nicht mehr zur Auswahl im Auslieferungsportal angeboten und sind automatisch in der Auslieferung hinterlegt.<br>
              Wird ein ausgewähltes Paket als <b>kostenlos</b> markiert, steht es dem Kunden weiterhin frei das vorausgewählte oder ein beliebiges anderes Paket zu buchen.<br><br>
              Die hier getroffene Auswahl kann beim Starten des Auslieferungsportals nochmals überprüft und bei Bedarf geändert werden.
            </p>
          </mia-notice>

          <mia-wait
            for="loading car sell customer portal packages"
            class="mt-8"
          >
            <template #waiting>
              <div class="flex items-center justify-center py-8">
                <mia-wave-loader />
              </div>
            </template>

            <template #default>
              <div class="grid grid-cols-12 gap-x-6 gap-y-1">
                <div class="col-span-12">
                  <label
                    for="car-sell-customer-portal-payment-method"
                    class="m-0 p-0 tracking-wider text-gray-800"
                  >1. Zahlmethode</label>
                </div>
                <div class="col-span-10">
                  <mia-feature-select
                    id="car-sell-customer-portal-payment-method"
                    :value="form.customer_portal.item_payment_method"
                    :errors="getErrors('customer_portal.item_payment_method')"
                    @input="onCustomerPortalFormInput('item_payment_method', $event)"
                  >
                    <template #options>
                      <option value="no_choice">
                        Bitte wählen
                      </option>
                      <option value="">
                        Ohne Vorauswahl
                      </option>
                      <option
                        v-for="paymentMethod in customerPortalPackages.payment_methods"
                        :key="paymentMethod.id"
                        :value="paymentMethod.id"
                      >
                        {{ paymentMethod.title }}
                      </option>
                    </template>
                  </mia-feature-select>
                </div>
                <div class="col-span-2" />
                <div class="col-span-12 pt-4">
                  <label
                    for="car-sell-customer-portal-payment-method"
                    class="m-0 p-0 tracking-wider text-gray-800"
                  >2. Auslieferungspaket</label>
                </div>
                <div class="col-span-10">
                  <mia-feature-select
                    id="car-sell-customer-portal-delivery-package"
                    :value="form.customer_portal.item_delivery_package"
                    :errors="getErrors('customer_portal.item_delivery_package')"
                    @input="onCustomerPortalFormInput('item_delivery_package', $event)"
                  >
                    <template #options>
                      <option value="no_choice">
                        Bitte wählen
                      </option>
                      <option value="">
                        Ohne Vorauswahl
                      </option>
                      <option
                        v-for="deliveryPackage in customerPortalPackages.delivery_packages"
                        :key="deliveryPackage.id"
                        :value="deliveryPackage.id"
                      >
                        {{ deliveryPackage.title }}
                      </option>
                    </template>
                  </mia-feature-select>
                </div>
                <div class="col-span-2">
                  <mia-feature-toggle
                    label="kostenlos"
                    :value="form.customer_portal.item_is_delivery_package_free === 'Y'"
                    :errors="getErrors('customer_portal.item_is_delivery_package_free')"
                    :disabled="!form.customer_portal.item_delivery_package || form.customer_portal.item_delivery_package === 'no_choice'"
                    @input="onCustomerPortalFormInput('item_is_delivery_package_free', $event ? 'Y' : 'N')"
                  />
                </div>
                <div class="col-span-12 pt-4">
                  <label
                    for="car-sell-customer-portal-payment-method"
                    class="m-0 p-0 tracking-wider text-gray-800"
                  >3. Zulassungspaket</label>
                </div>
                <div class="col-span-10">
                  <mia-feature-select
                    id="car-sell-customer-portal-registration-package"
                    :value="form.customer_portal.item_registration_package"
                    :errors="getErrors('customer_portal.item_registration_package')"
                    @input="onCustomerPortalFormInput('item_registration_package', $event)"
                  >
                    <template #options>
                      <option value="no_choice">
                        Bitte wählen
                      </option>
                      <option value="">
                        Ohne Vorauswahl
                      </option>
                      <option
                        v-for="registrationPackage in customerPortalPackages.registration_packages"
                        :key="registrationPackage.id"
                        :value="registrationPackage.id"
                      >
                        {{ registrationPackage.title }}
                      </option>
                    </template>
                  </mia-feature-select>
                </div>
                <div class="col-span-2">
                  <mia-feature-toggle
                    label="kostenlos"
                    :value="form.customer_portal.item_is_registration_package_free === 'Y'"
                    :errors="getErrors('customer_portal.item_is_registration_package_free')"
                    :disabled="!form.customer_portal.item_registration_package || form.customer_portal.item_registration_package === 'no_choice'"
                    @input="onCustomerPortalFormInput('item_is_registration_package_free', $event ? 'Y' : 'N')"
                  />
                </div>
                <template v-if="form.customer_portal.item_registration_package === 'regional'">
                  <div class="col-span-1 pt-4" />
                  <div class="col-span-9 pt-4">
                    <mia-feature-select
                      :value="form.customer_portal.item_registration_district"
                      :errors="getErrors('customer_portal.item_registration_district')"
                      required
                      label="Landkreis"
                      @input="onCustomerPortalFormInput('item_registration_district', $event)"
                    >
                      <template #options>
                        <option value="">
                          Bitte wählen
                        </option>
                        <option
                          v-for="registrationDistrict in customerPortalPackages.registration_districts"
                          :key="registrationDistrict.id"
                          :value="registrationDistrict.id"
                        >
                          {{ registrationDistrict.id }} - {{ registrationDistrict.title }}
                        </option>
                      </template>
                    </mia-feature-select>
                  </div>
                  <div class="col-span-2 pt-4" />
                </template>
                <div class="col-span-12 pt-4">
                  <label
                    for="car-sell-customer-tyre-package"
                    class="m-0 p-0 tracking-wider text-gray-800"
                  >4. Räderpaket</label>
                </div>
                <div class="col-span-10">
                  <mia-feature-select
                    id="car-sell-customer-tyre-package"
                    :value="form.customer_portal.item_tyre_package"
                    :errors="getErrors('customer_portal.item_tyre_package')"
                    @input="onCustomerPortalFormInput('item_tyre_package', $event)"
                  >
                    <template #options>
                      <option value="no_choice">
                        Bitte wählen
                      </option>
                      <option value="">
                        Ohne Vorauswahl
                      </option>
                      <option
                        v-for="tyrePackage in customerPortalPackages.tyre_packages"
                        :key="tyrePackage.id"
                        :value="tyrePackage.id"
                      >
                        {{ tyrePackage.title }}
                      </option>
                    </template>
                  </mia-feature-select>
                </div>
                <div class="col-span-2">
                  <mia-feature-toggle
                    label="kostenlos"
                    :value="form.customer_portal.item_is_tyre_package_free === 'Y'"
                    :errors="getErrors('customer_portal.item_is_tyre_package_free')"
                    :disabled="!form.customer_portal.item_tyre_package || form.customer_portal.item_tyre_package === 'no_choice'"
                    @input="onCustomerPortalFormInput('item_is_tyre_package_free', $event ? 'Y' : 'N')"
                  />
                </div>
                <template v-if="form.customer_portal.item_tyre_package === 'offer'">
                  <div class="col-span-1 pt-4" />
                  <div class="col-span-9 pt-4">
                    <mia-feature-select
                      :value="form.customer_portal.item_tyre_quality"
                      :errors="getErrors('customer_portal.item_tyre_quality')"
                      label="Qualität"
                      @input="onCustomerPortalFormInput('item_tyre_quality', $event)"
                    >
                      <template #options>
                        <option value="">
                          Bitte wählen
                        </option>
                        <option
                          v-for="tyreQuality in customerPortalPackages.tyre_qualities"
                          :key="tyreQuality.id"
                          :value="tyreQuality.id"
                        >
                          {{ tyreQuality.title }}
                        </option>
                      </template>
                    </mia-feature-select>
                  </div>
                  <div class="col-span-2 pt-4" />
                  <div class="col-span-1 pt-4" />
                  <div class="col-span-9 pt-4">
                    <mia-feature-select
                      :value="form.customer_portal.item_rim_material"
                      :errors="getErrors('customer_portal.item_rim_material')"
                      label="Material"
                      @input="onCustomerPortalFormInput('item_rim_material', $event)"
                    >
                      <template #options>
                        <option value="">
                          Bitte wählen
                        </option>
                        <option
                          v-for="rimMaterial in customerPortalPackages.rim_materials"
                          :key="rimMaterial.id"
                          :value="rimMaterial.id"
                        >
                          {{ rimMaterial.title }}
                        </option>
                      </template>
                    </mia-feature-select>
                  </div>
                  <div class="col-span-2 pt-4" />
                  <template v-if="form.customer_portal.item_rim_material === 'aluminum'">
                    <div class="col-span-1 pt-4" />
                    <div class="col-span-9 pt-4">
                      <mia-feature-select
                        :value="form.customer_portal.item_aluminum_rim_color"
                        :errors="getErrors('customer_portal.item_aluminum_rim_color')"
                        label="Farbe"
                        @input="onCustomerPortalFormInput('item_aluminum_rim_color', $event)"
                      >
                        <template #options>
                          <option value="">
                            Bitte wählen
                          </option>
                          <option
                            v-for="aluminiumRimColor in customerPortalPackages.aluminum_rim_colors"
                            :key="aluminiumRimColor.id"
                            :value="aluminiumRimColor.id"
                          >
                            {{ aluminiumRimColor.title }}
                          </option>
                        </template>
                      </mia-feature-select>
                    </div>
                    <div class="col-span-2 pt-4" />
                    <div class="col-span-1 pt-4" />
                    <div class="col-span-9 pt-4">
                      <mia-feature-select
                        :value="form.customer_portal.item_aluminum_rim_type"
                        :errors="getErrors('customer_portal.item_aluminum_rim_type')"
                        label="Art"
                        @input="onCustomerPortalFormInput('item_aluminum_rim_type', $event)"
                      >
                        <template #options>
                          <option value="">
                            Bitte wählen
                          </option>
                          <option
                            v-for="aluminiumRimType in customerPortalPackages.aluminum_rim_types"
                            :key="aluminiumRimType.id"
                            :value="aluminiumRimType.id"
                          >
                            {{ aluminiumRimType.title }}
                          </option>
                        </template>
                      </mia-feature-select>
                    </div>
                    <div class="col-span-2 pt-4" />
                    <div class="col-span-1 pt-4" />
                    <div class="col-span-9 pt-4">
                      <mia-feature-select
                        :value="form.customer_portal.item_aluminum_rim_design"
                        :errors="getErrors('customer_portal.item_aluminum_rim_design')"
                        label="Design"
                        @input="onCustomerPortalFormInput('item_aluminum_rim_design', $event)"
                      >
                        <template #options>
                          <option value="">
                            Bitte wählen
                          </option>
                          <option
                            v-for="aluminiumRimDesign in customerPortalPackages.aluminum_rim_designs"
                            :key="aluminiumRimDesign.id"
                            :value="aluminiumRimDesign.id"
                          >
                            {{ aluminiumRimDesign.title }}
                          </option>
                        </template>
                      </mia-feature-select>
                    </div>
                    <div class="col-span-2 pt-4" />
                  </template>
                </template>
              </div>
            </template>
          </mia-wait>
        </mia-form-section>
      </mia-has-feature>
    </template>

    <template #actions="{ sticky }">
      <div class="w-full flex items-center justify-between pl-2">
        <button
          v-tooltip="{
            content: `Zum ${sticky ? 'Ende' : 'Anfang'} des Formulars`,
            delay: { show: 1000, hide: 0 },
            offset: 10
          }"
          type="button"
          class="hover:text-orange-mia focus:outline-none"
          @click="() => sticky ? scrollToBottom() : scrollToTop()"
        >
          <mia-streamline-icon
            class="w-5 h-5 fill-current"
            :src="sticky ? 'arrow-down-1' : 'arrow-up-1'"
          />
        </button>

        <mia-submit-button
          size="small"
          :is-loading="$wait.is('selling car')"
          label="Verkaufsmeldung erstellen"
        />
      </div>
    </template>
  </mia-form>
</template>

<style lang="scss">
.form-input,
.form-select {
  &:required,
  &.is-required {
    border-left-width: calc(theme('width.px') * 3);
    border-left-color: theme('borderColor.orange-mia');
  }

  &.has-errors {
    @apply shadow-outline-error;
  }
}
</style>
